/**
 * Sièges exiPlan d'une organisation du studio (mode `registre`, lot O3) — liste nominative et
 * gestes : attribuer, suspendre, réactiver, retirer. Cellule de planche B, posée par la page hôte
 * dans la grille de l'onglet Licences (ou de l'écran « pas de siège » d'un rôle de gestion).
 *
 * La liste nominative (`seats.sieges`) n'est servie qu'aux rôles de gestion (`proprietaire`,
 * `administrateur`, `facturation`) : sans elle le composant ne rend rien. Les candidats à
 * l'attribution viennent de la liste des membres (par oid) ; un siège suspendu se réactive, il ne
 * se réattribue pas. Jamais de geste sur son propre siège (409 `SIEGE_SOI_MEME` côté serveur).
 * Le serveur reste seul juge (hiérarchie des rôles, limite, grâce de licence) : chaque refus est
 * traduit par `messageModeleOrganisation`. `lectureSeule` (registre dégradé) désactive tout.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { messageModeleOrganisation } from '../lib/org-errors.js';
import { candidatsSiege, nomMembre } from '../lib/sieges.js';
import { appAlert, appConfirm } from '../dialogs/service.js';
import {
  BTN_GHOST,
  BTN_GHOST_SM,
  CELL_PLANCHE,
  COL_ACTIONS,
  COL_ROLE,
  COL_STATUS,
  EYEBROW,
  FORM_FIELD,
  FORM_LABEL,
  FORM_ROW,
  SELECT,
  TAG,
  TD,
  TH,
} from '../ui/theme.js';

/** Littéraux dans l'objet — le gate `keys.test.js` ne voit pas une clé de gabarit calculée. */
const SOURCE_LABEL_KEYS = {
  attribution: 'organization.seats.manage.source.attribution',
  creation: 'organization.seats.manage.source.creation',
  migration: 'organization.seats.manage.source.migration',
  auto: 'organization.seats.manage.source.auto',
};

const STATUS_LABEL_KEYS = {
  attribue: 'organization.seats.manage.status.attribue',
  suspendu: 'organization.seats.manage.status.suspendu',
};

/**
 * @param {object} props
 * @param {string} props.organizationId
 * @param {{ used: number, limit: number|null, sieges?: Array<{ oid: string, userId: string|null,
 *   name: string|null, statut: 'attribue'|'suspendu', source: string }> }} props.seats
 * @param {Array<{ oid: string, userId: string|null, name?: string|null, email?: string|null,
 *   statut: string, siege: string|null }>} props.members membres du registre, par oid
 * @param {string} [props.currentUserId] compte connecté : pas de geste sur son propre siège
 * @param {boolean} [props.lectureSeule] registre dégradé
 * @param {boolean} [props.attribution] formulaire d'attribution offert (faux quand la liste des
 *   membres est fermée à l'appelant : gestionnaire sans siège)
 * @param {() => void} props.onChanged rechargement après un geste réussi
 */
export function SeatsPanel({
  organizationId,
  seats,
  members,
  currentUserId,
  currentRole,
  lectureSeule = false,
  attribution = true,
  onChanged,
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [cible, setCible] = useState('');

  if (!seats?.sieges) return null;

  const parOid = new Map(members.map((membre) => [membre.oid, membre]));
  // `facturation` ne s'attribue jamais de siège (403 FACTURATION_SANS_SIEGE) : pas dans ses candidats.
  const candidats = candidatsSiege(members).filter(
    (membre) => !(currentRole === 'facturation' && membre.userId === currentUserId),
  );
  const bloque = busy || lectureSeule;
  const base = `/organizations/${organizationId}/sieges`;

  const nomDuSiege = (siege) =>
    siege.name ||
    nomMembre(parOid.get(siege.oid) ?? {}) ||
    t('organization.seats.manage.unknownMember');

  const executer = async (geste, titre, cle) => {
    setBusy(true);
    try {
      await geste();
      await onChanged();
    } catch (error) {
      await appAlert(messageModeleOrganisation(error, t) ?? t(cle, { message: error.message }), {
        title: t(titre),
      });
    } finally {
      setBusy(false);
    }
  };

  const attribuer = async (event) => {
    event.preventDefault();
    if (!cible) return;
    await executer(
      async () => {
        await api.post(base, { oid: cible });
        setCible('');
      },
      'organization.seats.manage.assignFailedTitle',
      'organization.seats.manage.assignFailed',
    );
  };

  const changerStatut = (siege, statut) =>
    executer(
      () => api.patch(`${base}/${siege.oid}`, { statut }),
      'organization.seats.manage.changeFailedTitle',
      'organization.seats.manage.changeFailed',
    );

  const retirer = async (siege) => {
    const confirme = await appConfirm(
      t('organization.seats.manage.removeConfirm', { name: nomDuSiege(siege) }),
      { confirmLabel: t('organization.seats.manage.removeConfirmLabel') },
    );
    if (!confirme) return;
    await executer(
      () => api.delete(`${base}/${siege.oid}`),
      'organization.seats.manage.changeFailedTitle',
      'organization.seats.manage.changeFailed',
    );
  };

  return (
    <div className={`${CELL_PLANCHE} flex flex-col gap-4 bg-paper p-6`}>
      <span className="ticks" aria-hidden="true" />
      <p className={EYEBROW}>{t('organization.seats.manage.eyebrow')}</p>

      {seats.sieges.length === 0 ? (
        <p className="text-sm text-ink-soft">{t('organization.seats.manage.empty')}</p>
      ) : (
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            <col className="w-auto" />
            <col className={COL_STATUS} />
            <col className={COL_ROLE} />
            <col className={COL_ACTIONS} />
          </colgroup>
          <thead>
            <tr>
              <th className={TH}>{t('organization.seats.manage.columnMember')}</th>
              <th className={TH}>{t('organization.seats.manage.columnStatus')}</th>
              <th className={TH}>{t('organization.seats.manage.columnSource')}</th>
              <th className={TH}>{t('organization.seats.manage.columnActions')}</th>
            </tr>
          </thead>
          <tbody>
            {seats.sieges.map((siege) => {
              const nom = nomDuSiege(siege);
              const estMoi = Boolean(currentUserId) && siege.userId === currentUserId;
              return (
                <tr key={siege.oid}>
                  <td className={TD}>
                    <span className="block text-ink">{nom}</span>
                  </td>
                  <td className={TD}>
                    <span className={`${TAG} border-line text-ink-soft`}>
                      {STATUS_LABEL_KEYS[siege.statut]
                        ? t(STATUS_LABEL_KEYS[siege.statut])
                        : siege.statut}
                    </span>
                  </td>
                  <td className={TD}>
                    <span className="text-xs text-ink-soft">
                      {SOURCE_LABEL_KEYS[siege.source] ? t(SOURCE_LABEL_KEYS[siege.source]) : ''}
                    </span>
                  </td>
                  <td className={TD}>
                    {/* Sur son propre siège : ni retrait ni suspension (409 SIEGE_SOI_MEME), mais la
                        réactivation reste offerte (le serveur tranche : propriétaire seulement). */}
                    {(!estMoi || siege.statut === 'suspendu') && (
                      <div className="flex flex-wrap gap-2">
                        {siege.statut === 'attribue' ? (
                          <button
                            type="button"
                            className={BTN_GHOST_SM}
                            aria-label={t('organization.seats.manage.suspendAria', { name: nom })}
                            disabled={bloque}
                            onClick={() => changerStatut(siege, 'suspendu')}
                          >
                            {t('organization.seats.manage.suspend')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={BTN_GHOST_SM}
                            aria-label={t('organization.seats.manage.reactivateAria', {
                              name: nom,
                            })}
                            disabled={bloque}
                            onClick={() => changerStatut(siege, 'attribue')}
                          >
                            {t('organization.seats.manage.reactivate')}
                          </button>
                        )}
                        {!estMoi && (
                          <button
                            type="button"
                            className={BTN_GHOST_SM}
                            aria-label={t('organization.seats.manage.removeAria', { name: nom })}
                            disabled={bloque}
                            onClick={() => retirer(siege)}
                          >
                            {t('organization.seats.manage.remove')}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <p className="text-xs text-ink-faint">{t('organization.seats.manage.removeHint')}</p>

      {attribution && (
        <form onSubmit={attribuer} className={FORM_ROW}>
          <label className={FORM_FIELD}>
            <span className={FORM_LABEL}>{t('organization.seats.manage.assignLabel')}</span>
            <select
              className={SELECT}
              value={cible}
              onChange={(event) => setCible(event.target.value)}
              disabled={bloque || candidats.length === 0}
            >
              <option value="">{t('organization.seats.manage.assignPlaceholder')}</option>
              {candidats.map((membre) => (
                <option key={membre.oid} value={membre.oid}>
                  {nomMembre(membre) ?? t('organization.seats.manage.unknownMember')}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className={BTN_GHOST} disabled={bloque || !cible}>
            {t('organization.seats.manage.assign')}
          </button>
        </form>
      )}
      {attribution && candidats.length === 0 && (
        <p className="text-xs text-ink-faint">{t('organization.seats.manage.assignNone')}</p>
      )}
    </div>
  );
}
